import { useState, useMemo, useCallback, useRef, type ReactNode } from "react";
import {
  Modal,
  Stack,
  SimpleGrid,
  Button,
  Group,
  Box,
  Title,
  Accordion,
  Badge,
  Text,
  Alert,
  Checkbox,
  Select,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQuery } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { api } from "../../lib";
import { errorRefLabel } from "../../utils/formatters";
import {
  CertaintyTextInput,
  CertaintyTextarea,
  CertaintySelect,
  CertaintyDateInput,
  CertaintyRichText,
} from "../accessioning/CertaintyInput";
import {
  useStates,
  useCities,
  useAccounts,
  usePhysicians,
  useSurgeryCenters,
  useLabs,
  useGrossCodes,
} from "../../hooks/useReferenceData";
import {
  useUpdatePatientInfo,
  useDirectCorrectionAmendment,
  useDirectCorrectionPreview,
  useSignedCorrectionPrefixContext,
} from "../../hooks/usePatientInfo";
import { useHasPermission } from "../../hooks/useHasPermission";
import {
  useUpdateCorrectionRequest,
  useActOnCorrectionRequest,
} from "../../hooks/useCorrectionRequests";
import { useClientIdentityPreview } from "../../hooks/useClientIdentityPreview";
import type { ClientIdentityDirective } from "../../types/clientIdentity";
import ClientIdentityImpactPanel from "./ClientIdentityImpactPanel";
import PatientInfoFieldHint from "./PatientInfoFieldHint";
import SignedCorrectionPrefixFields, {
  type SignedCorrectionPrefixErrors,
  type SignedCorrectionPrefixSelection,
  validateSignedCorrectionPrefixSelection,
} from "./SignedCorrectionPrefixFields";
import { FORM_PATH_TO_CHANGES_KEY } from "./patientInfoFieldPaths";
import {
  parseCaseNumber,
  validateCaseNumberSequence,
} from "../../utils/caseNumberSequence";
import { formatDateForApi, parseDateString } from "../../utils/dates";
import {
  patientInfoService,
  type DirectCorrectionAmendmentBody,
  type DirectCorrectionPreviewResponse,
} from "../../services/patientInfoService";
import type {
  ReportContentVersion,
  ReportPreviewEvidence,
} from "../../services/signOff.service";
import type { SpecimenGrossingData } from "../pathologist-review/GrossingCenter";
import { ReportPreviewModal } from "../workflows/ReportPreviewModal";
import { DirectCorrectionGrossingScreen } from "./DirectCorrectionGrossingScreen";
import {
  buildDirectCorrectionGrossingDataMap,
  buildDirectCorrectionSpecimenChanges,
} from "./directCorrectionGrossing";

// ==================== TYPES ====================

interface UpdatePatientInfoModalProps {
  caseId: string;
  opened: boolean;
  onClose: () => void;
  isSignedOff: boolean;
  userRole?: string;
  caseData?: any;
  editingCorrectionRequestId?: string;
  initialProposedChanges?: Record<string, { before: any; after: any }>;
  initialCaseErrorReportId?: string | null;
  reviewMode?: boolean;
  correctionAmendmentMode?: boolean;
  onDiscard?: () => void;
}

type CorrectionModalScreen = "details" | "grossing" | "preview";

// ==================== HELPERS ====================

function sanitizeOptions<T extends { value: string }>(
  options: T[] | undefined | null
): T[] {
  if (!Array.isArray(options)) return [];
  return options.filter(
    (opt): opt is T =>
      opt != null &&
      typeof opt === "object" &&
      "value" in opt &&
      opt.value != null &&
      opt.value !== ""
  );
}

function isFilled(v: unknown): boolean {
  if (v === null || v === undefined || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  // Rich-text empty states: "<p></p>" or just whitespace/tags
  if (typeof v === "string") {
    const stripped = v.replace(/<[^>]*>/g, "").trim();
    return stripped.length > 0;
  }
  return true;
}

function countFilled(values: unknown[]): number {
  return values.filter(isFilled).length;
}

function filterUnchanged(
  obj: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  );
}

function diffClearableId(
  newValue: string | null | undefined,
  originalValue: string | null | undefined
): string | null | undefined {
  const normalizedNewValue = newValue || null;
  const normalizedOriginalValue = originalValue || null;
  return normalizedNewValue === normalizedOriginalValue
    ? undefined
    : normalizedNewValue;
}

// ==================== COMPONENT ====================

export default function UpdatePatientInfoModal({
  caseId,
  opened,
  onClose,
  isSignedOff,
  caseData,
  editingCorrectionRequestId,
  initialProposedChanges,
  initialCaseErrorReportId,
  reviewMode,
  correctionAmendmentMode,
  onDiscard,
}: UpdatePatientInfoModalProps) {
  const isEditMode = !!editingCorrectionRequestId;
  const isReviewMode = !!reviewMode && isEditMode;
  const isCorrectionAmendmentMode = !!correctionAmendmentMode;
  // Both reviewMode and correctionAmendmentMode share the same "correction" UX
  const isCorrectionUX = isReviewMode || isCorrectionAmendmentMode;
  // Show the "link to error" select when creating or editing a correction request (not in review/amendment modes)
  const showLinkError = !isCorrectionUX && (isSignedOff || isEditMode);
  const clientProviderFieldsLocked = useMemo(
    () => isSignedOff && !isCorrectionAmendmentMode,
    [isCorrectionAmendmentMode, isSignedOff]
  );

  const [linkedErrorId, setLinkedErrorId] = useState<string | null>(null);
  const [clientIdentityDirective, setClientIdentityDirective] =
    useState<ClientIdentityDirective>({ mode: "keep" });
  const [correctionScreen, setCorrectionScreen] =
    useState<CorrectionModalScreen>("details");
  const [grossingDataMap, setGrossingDataMap] = useState<
    Map<string, SpecimenGrossingData>
  >(new Map());
  const [pendingCorrectionBody, setPendingCorrectionBody] =
    useState<DirectCorrectionAmendmentBody | null>(null);
  const [correctionPreview, setCorrectionPreview] =
    useState<DirectCorrectionPreviewResponse | null>(null);
  const preserveCorrectionDraftOnNextEnterRef = useRef(false);

  const hint = (formPath: string): ReactNode => {
    if (!isReviewMode || !initialProposedChanges) return undefined;
    const key = FORM_PATH_TO_CHANGES_KEY[formPath];
    if (!key) return undefined;
    const change = initialProposedChanges[key];
    if (!change) return undefined;
    const before = String(change.before ?? "");
    if (!before.replace(/<[^>]*>/g, "").trim()) return undefined;
    return (
      <Box
        component="span"
        className="rich-text-display"
        dangerouslySetInnerHTML={{ __html: `Current: ${before}` }}
      />
    );
  };
  const mutation = useUpdatePatientInfo(caseId);
  const updateMutation = useUpdateCorrectionRequest();
  const actMutation = useActOnCorrectionRequest();
  const directMutation = useDirectCorrectionAmendment(caseId);
  const directPreviewMutation = useDirectCorrectionPreview(caseId);
  const canReassignPrefix = useHasPermission("cases.prefix.reassign");
  const signedPrefixContextQuery = useSignedCorrectionPrefixContext(
    caseId,
    opened && isCorrectionAmendmentMode && canReassignPrefix
  );
  const reportTimezoneCorrectionQuery = useQuery({
    queryKey: ["report-timezone-correction-eligibility", caseId],
    queryFn: () =>
      patientInfoService.getReportTimezoneCorrectionEligibility(caseId),
    enabled: opened && isCorrectionAmendmentMode,
  });
  const reportTimezoneCorrectionContext = Array.isArray(
    reportTimezoneCorrectionQuery.data
  )
    ? undefined
    : reportTimezoneCorrectionQuery.data;

  const { data: caseErrors = [] } = useQuery({
    queryKey: ["caseErrors", caseId],
    queryFn: () =>
      api.get<
        Array<{
          id: string;
          refNumber?: number;
          description: string;
          discrepancy?: { error: string };
          errorType?: { error: string };
          status: string;
        }>
      >(`/workflows/errors/case/${caseId}`),
    enabled: showLinkError && opened,
  });
  const { data: grossCodes = [] } = useGrossCodes({
    enabled: opened && isCorrectionAmendmentMode,
  });

  const pendingErrorOptions = useMemo(
    () =>
      caseErrors
        .filter((e) => e.status === "pending")
        .map((e) => ({
          value: e.id,
          label: `${
            errorRefLabel(e.refNumber) ? `${errorRefLabel(e.refNumber)} — ` : ""
          }${
            e.discrepancy?.error ||
            e.errorType?.error ||
            e.description ||
            "Error"
          }`,
        })),
    [caseErrors]
  );

  const handleLinkedErrorChange = useCallback((value: string | null) => {
    setLinkedErrorId(value);
  }, []);

  const isLoading =
    directPreviewMutation.isPending ||
    (isReviewMode
      ? actMutation.isPending
      : isCorrectionAmendmentMode
      ? directMutation.isPending
      : isEditMode
      ? updateMutation.isPending
      : mutation.isPending);

  const op = caseData?.patient;

  const [openedSections, setOpenedSections] = useState<string[]>([
    "patient",
    "primaryInsurance",
    "clientProvider",
    "caseInfo",
  ]);

  // ── Form ──────────────────────────────────────────────────────────────────

  const latestFormValues = useMemo(
    () => ({
      patient: {
        firstName: op?.firstName || "",
        lastName: op?.lastName || "",
        dateOfBirth: parseDateString(op?.dateOfBirth),
        sex: op?.sex || "",
        medicalRecordNumber: op?.medicalRecordNumber || "",
        mobilePhone: op?.mobilePhone || "",
        homePhone: op?.homePhone || "",
        workPhone: op?.workPhone || "",
        email: op?.email || "",
        address: {
          street: op?.address?.street || "",
          state: op?.address?.state || "",
          city: op?.address?.city || "",
          zipCode: op?.address?.zipCode || "",
        },
      },
      primaryInsurance: {
        policyNumber: caseData?.primaryInsurance?.policyNumber || "",
        authorizationNumber:
          caseData?.primaryInsurance?.authorizationNumber || "",
        groupNumber: caseData?.primaryInsurance?.groupNumber || "",
        groupName: caseData?.primaryInsurance?.groupName || "",
        patientRelationToInsured:
          caseData?.primaryInsurance?.patientRelationToInsured || "",
        address: {
          street: caseData?.primaryInsurance?.address?.street || "",
          state: caseData?.primaryInsurance?.address?.state || "",
          city: caseData?.primaryInsurance?.address?.city || "",
          zipCode: caseData?.primaryInsurance?.address?.zipCode || "",
        },
      },
      secondaryInsurance: {
        policyNumber: caseData?.secondaryInsurance?.policyNumber || "",
        authorizationNumber:
          caseData?.secondaryInsurance?.authorizationNumber || "",
        groupNumber: caseData?.secondaryInsurance?.groupNumber || "",
        groupName: caseData?.secondaryInsurance?.groupName || "",
        patientRelationToInsured:
          caseData?.secondaryInsurance?.patientRelationToInsured || "",
        address: {
          street: caseData?.secondaryInsurance?.address?.street || "",
          state: caseData?.secondaryInsurance?.address?.state || "",
          city: caseData?.secondaryInsurance?.address?.city || "",
          zipCode: caseData?.secondaryInsurance?.address?.zipCode || "",
        },
      },
      caseInfo: {
        sendingPracticeId: caseData?.sendingPracticeId ?? null,
        physicianId: caseData?.physicianId ?? null,
        surgeryCenterLabId: caseData?.surgeryCenterLabId ?? null,
        submittingLabId: caseData?.submittingLabId ?? null,
        externalCaseNumber: caseData?.externalCaseNumber || "",
        dateReceived: parseDateString(caseData?.dateReceived),
        dateCollected: parseDateString(caseData?.dateCollected),
        // Rich-text fields — stored as HTML
        clinicalHistory: caseData?.clinicalHistoryNotes || "",
        indications: caseData?.indications || "",
        impressions: caseData?.impressions || "",
        ruleOuts: caseData?.ruleOuts || "",
        // Originating facility (pass-through / consultation / TC cases)
        originatingPracticeId: caseData?.originatingPracticeId || "",
        originatingPhysicianId: caseData?.originatingPhysicianId || "",
        originatingSurgeryCenterId: caseData?.originatingSurgeryCenterId || "",
        // Consultation fields
        consultationReason: caseData?.consultationReason || "",
        consultationOriginalDiagnosis:
          caseData?.consultationOriginalDiagnosis || "",
        consultationClinicalContext:
          caseData?.consultationClinicalContext || "",
        requestingPathologist: caseData?.requestingPathologist || "",
        // TC shipping fields
        shippingPathologist: caseData?.shippingPathologist || "",
        shippingStreet: caseData?.shippingStreet || "",
        shippingState: caseData?.shippingState || "",
        shippingCity: caseData?.shippingCity || "",
        shippingZipCode: caseData?.shippingZipCode || "",
      },
      prefixReassignment: {
        targetPrefixId: "",
        caseNumberRemainder: "",
      },
      reportTimezoneCorrection: false,
      note: "",
      reason: "",
    }),
    [caseData, op]
  );

  const form = useForm({
    initialValues: latestFormValues,
    validateInputOnBlur: true,

    validate: (values) => {
      const errors: Record<string, string> = {};

      if (isCorrectionUX && !isFilled(values.reason))
        errors.reason = "Reason for modification is required";

      if (isCorrectionAmendmentMode) {
        const prefixErrors = validateSignedCorrectionPrefixSelection(
          values.prefixReassignment,
          signedPrefixContextQuery.data
        );
        Object.entries(prefixErrors).forEach(([field, message]) => {
          errors[`prefixReassignment.${field}`] = message;
        });
      }

      // Cannot clear a field that was originally set
      if (op?.firstName && !values.patient.firstName)
        errors["patient.firstName"] = "First name is required";
      if (op?.lastName && !values.patient.lastName)
        errors["patient.lastName"] = "Last name is required";
      if (op?.dateOfBirth && !values.patient.dateOfBirth)
        errors["patient.dateOfBirth"] = "Date of birth is required";
      if (op?.sex && !values.patient.sex)
        errors["patient.sex"] = "Sex is required";
      if (op?.medicalRecordNumber && !values.patient.medicalRecordNumber)
        errors["patient.medicalRecordNumber"] =
          "Medical record number is required";

      // Email format
      const email = values.patient.email;
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        errors["patient.email"] =
          "Enter a valid email address (e.g., name@example.com)";

      // City requires state for patient address
      const isCityModified =
        values.patient.address.city !== (op?.address?.city || "");
      const isStateModified =
        values.patient.address.state !== (op?.address?.state || "");

      if (
        (isCityModified || isStateModified) &&
        values.patient.address.city &&
        !values.patient.address.state
      ) {
        errors["patient.address.city"] = "Select a state first";
      }

      return errors;
    },
  });

  const handleReasonChange = useCallback(
    (val: string) => {
      form.setFieldValue("reason", val);
    },
    [form]
  );

  const handleSignedPrefixSelectionChange = useCallback(
    (selection: SignedCorrectionPrefixSelection) => {
      form.setFieldValue("prefixReassignment", selection);
      form.clearFieldError("prefixReassignment.targetPrefixId");
      form.clearFieldError("prefixReassignment.caseNumberRemainder");
    },
    [form]
  );

  const handleOriginalDiagnosisChange = useCallback(
    (val: string) => {
      form.setFieldValue("caseInfo.consultationOriginalDiagnosis", val);
    },
    [form]
  );

  const handleClientIdentityDirectiveChange = useCallback(
    (directive: ClientIdentityDirective) => {
      setClientIdentityDirective(directive);
    },
    []
  );

  const handleSendingPracticeChange = useCallback(
    (value: string | null) => {
      form.setFieldValue("caseInfo.sendingPracticeId", value);
      setClientIdentityDirective({ mode: "keep" });
    },
    [form]
  );

  const handlePhysicianChange = useCallback(
    (value: string | null) => {
      form.setFieldValue("caseInfo.physicianId", value);
      setClientIdentityDirective({ mode: "keep" });
    },
    [form]
  );

  const handleSurgeryCenterChange = useCallback(
    (value: string | null) => {
      form.setFieldValue("caseInfo.surgeryCenterLabId", value);
      setClientIdentityDirective({ mode: "keep" });
    },
    [form]
  );

  const handleSubmittingLabChange = useCallback(
    (value: string | null) => {
      form.setFieldValue("caseInfo.submittingLabId", value);
      setClientIdentityDirective({ mode: "keep" });
    },
    [form]
  );

  // ── Reference data ─────────────────────────────────────────────────────────

  const { data: states = [] } = useStates();

  // Patient address cities
  const patientState = form.values.patient.address.state;
  const { data: patientCities = [] } = useCities(patientState || "");

  // Primary insurance address cities
  const priInsState = form.values.primaryInsurance.address.state;
  const { data: priInsCities = [] } = useCities(priInsState || "");

  // Secondary insurance address cities
  const secInsState = form.values.secondaryInsurance.address.state;
  const { data: secInsCities = [] } = useCities(secInsState || "");

  // Originating facility selects
  const {
    data: practices = [],
    isFetching: practicesLoading,
    isError: practicesFailed,
    refetch: refetchPractices,
  } = useAccounts();
  const {
    data: physicians = [],
    isFetching: physiciansLoading,
    isError: physiciansFailed,
    refetch: refetchPhysicians,
  } = usePhysicians();
  const {
    data: originatingSurgeryCenters = [],
    isFetching: surgeryCentersLoading,
    isError: surgeryCentersFailed,
    refetch: refetchSurgeryCenters,
  } = useSurgeryCenters();
  const {
    data: labs = [],
    isFetching: labsLoading,
    isError: labsFailed,
    refetch: refetchLabs,
  } = useLabs();

  const accType = caseData?.accessioningType;
  const isTcOnly = accType === "tc_only";

  const clientIdentityCandidate = useMemo(
    () => ({
      sendingPracticeId: form.values.caseInfo.sendingPracticeId,
      physicianId: form.values.caseInfo.physicianId,
      surgeryCenterLabId: form.values.caseInfo.surgeryCenterLabId,
      submittingLabId: form.values.caseInfo.submittingLabId,
    }),
    [
      form.values.caseInfo.physicianId,
      form.values.caseInfo.sendingPracticeId,
      form.values.caseInfo.submittingLabId,
      form.values.caseInfo.surgeryCenterLabId,
    ]
  );

  const clientIdentityChanged = useMemo(
    () =>
      diffClearableId(
        clientIdentityCandidate.sendingPracticeId,
        caseData?.sendingPracticeId
      ) !== undefined ||
      diffClearableId(
        clientIdentityCandidate.physicianId,
        caseData?.physicianId
      ) !== undefined ||
      diffClearableId(
        clientIdentityCandidate.surgeryCenterLabId,
        caseData?.surgeryCenterLabId
      ) !== undefined ||
      diffClearableId(
        clientIdentityCandidate.submittingLabId,
        caseData?.submittingLabId
      ) !== undefined,
    [clientIdentityCandidate, caseData]
  );

  const clientIdentityPreview = useClientIdentityPreview(
    caseId,
    clientIdentityCandidate,
    opened && !isSignedOff && !isTcOnly && clientIdentityChanged
  );

  // ── Conditional case info fields ───────────────────────────────────────────

  const showOriginatingFacility = [
    "pass-through",
    "pass_through",
    "consultation",
    "tc-only",
    "tc_only",
  ].includes(accType);
  const showConsultationFields = accType === "consultation";

  // ── Field completion ───────────────────────────────────────────────────────

  const fieldCompletion = useMemo(() => {
    const v = form.values;

    const patientFields = [
      v.patient.firstName,
      v.patient.lastName,
      v.patient.dateOfBirth,
      v.patient.sex,
      v.patient.medicalRecordNumber,
      v.patient.mobilePhone,
      v.patient.homePhone,
      v.patient.workPhone,
      v.patient.email,
      v.patient.address.street,
      v.patient.address.state,
      v.patient.address.city,
      v.patient.address.zipCode,
    ];
    const patientReqsMet = [
      !op?.firstName || v.patient.firstName,
      !op?.lastName || v.patient.lastName,
      !op?.dateOfBirth || v.patient.dateOfBirth,
      !op?.sex || v.patient.sex,
      !op?.medicalRecordNumber || v.patient.medicalRecordNumber,
    ];

    const priFields = [
      v.primaryInsurance.policyNumber,
      v.primaryInsurance.authorizationNumber,
      v.primaryInsurance.groupNumber,
      v.primaryInsurance.groupName,
      v.primaryInsurance.patientRelationToInsured,
      v.primaryInsurance.address.street,
      v.primaryInsurance.address.state,
      v.primaryInsurance.address.city,
      v.primaryInsurance.address.zipCode,
    ];

    const secFields = [
      v.secondaryInsurance.policyNumber,
      v.secondaryInsurance.authorizationNumber,
      v.secondaryInsurance.groupNumber,
      v.secondaryInsurance.groupName,
      v.secondaryInsurance.patientRelationToInsured,
      v.secondaryInsurance.address.street,
      v.secondaryInsurance.address.state,
      v.secondaryInsurance.address.city,
      v.secondaryInsurance.address.zipCode,
    ];

    const caseFields: unknown[] = [
      v.caseInfo.externalCaseNumber,
      v.caseInfo.dateReceived,
      v.caseInfo.dateCollected,
      v.caseInfo.clinicalHistory,
      v.caseInfo.indications,
      v.caseInfo.impressions,
      v.caseInfo.ruleOuts,
    ];
    if (showOriginatingFacility) {
      caseFields.push(
        v.caseInfo.originatingPracticeId,
        v.caseInfo.originatingPhysicianId,
        v.caseInfo.originatingSurgeryCenterId
      );
    }
    if (showConsultationFields) {
      caseFields.push(
        v.caseInfo.consultationReason,
        v.caseInfo.consultationOriginalDiagnosis,
        v.caseInfo.consultationClinicalContext,
        v.caseInfo.requestingPathologist
      );
    }
    return {
      patient: {
        completed: countFilled(patientFields),
        total: patientFields.length,
        requiredComplete: patientReqsMet.every(Boolean),
      },
      primaryInsurance: {
        completed: countFilled(priFields),
        total: priFields.length,
        recommendedComplete: isFilled(v.primaryInsurance.policyNumber),
      },
      secondaryInsurance: {
        completed: countFilled(secFields),
        total: secFields.length,
      },
      caseInfo: {
        completed: countFilled(caseFields),
        total: caseFields.length,
      },
    };
  }, [form.values, showOriginatingFacility, showConsultationFields, op]);

  const specimenChanges = useMemo(
    () =>
      buildDirectCorrectionSpecimenChanges(
        caseData?.specimens,
        grossingDataMap
      ),
    [caseData?.specimens, grossingDataMap]
  );

  const handleGrossingChange = useCallback(
    (specimenId: string, data: Partial<SpecimenGrossingData>) => {
      setGrossingDataMap((current) => {
        const existing = current.get(specimenId);
        if (!existing) return current;
        const next = new Map(current);
        next.set(specimenId, { ...existing, ...data });
        return next;
      });
      setPendingCorrectionBody(null);
      setCorrectionPreview(null);
    },
    []
  );

  const handleOpenGrossing = useCallback(() => {
    setGrossingDataMap((current) => {
      const prefilled = buildDirectCorrectionGrossingDataMap(
        caseData?.specimens,
        grossCodes
      );
      if (current.size === 0) return prefilled;
      const hydrated = new Map(current);
      current.forEach((entry, specimenId) => {
        const prefilledEntry = prefilled.get(specimenId);
        if (!entry.grossCode && prefilledEntry?.grossCode) {
          hydrated.set(specimenId, {
            ...entry,
            grossCode: prefilledEntry.grossCode,
          });
        }
      });
      return hydrated;
    });
    setCorrectionScreen("grossing");
  }, [caseData?.specimens, grossCodes]);

  const handleReturnToCorrection = useCallback(() => {
    setCorrectionScreen("details");
  }, []);

  const handleReturnFromPreview = useCallback(() => {
    preserveCorrectionDraftOnNextEnterRef.current = true;
    setCorrectionScreen("details");
  }, []);

  // ── Reset when the modal starts opening ───────────────────────────────────

  const handleModalEnter = useCallback(() => {
    if (preserveCorrectionDraftOnNextEnterRef.current) {
      preserveCorrectionDraftOnNextEnterRef.current = false;
      return;
    }
    void Promise.all([
      refetchPractices(),
      refetchPhysicians(),
      refetchSurgeryCenters(),
      refetchLabs(),
    ]);
    form.setInitialValues(latestFormValues);
    form.setValues(latestFormValues);
    form.resetDirty(latestFormValues);
    form.resetTouched();
    form.clearErrors();
    setLinkedErrorId(initialCaseErrorReportId ?? null);
    setClientIdentityDirective({ mode: "keep" });
    setCorrectionScreen("details");
    setGrossingDataMap(
      buildDirectCorrectionGrossingDataMap(caseData?.specimens, grossCodes)
    );
    setPendingCorrectionBody(null);
    setCorrectionPreview(null);
    setOpenedSections([
      "patient",
      "primaryInsurance",
      "clientProvider",
      "caseInfo",
    ]);

    if (isEditMode && initialProposedChanges) {
      const p = initialProposedChanges;
      const get = (key: string) => p[key]?.after;

      const patch: Record<string, any> = {};

      if (get("patient.firstName") !== undefined)
        patch["patient.firstName"] = get("patient.firstName") ?? "";
      if (get("patient.lastName") !== undefined)
        patch["patient.lastName"] = get("patient.lastName") ?? "";
      if (get("patient.dateOfBirth") !== undefined)
        patch["patient.dateOfBirth"] = parseDateString(
          get("patient.dateOfBirth")
        );
      if (get("patient.sex") !== undefined)
        patch["patient.sex"] = get("patient.sex") ?? "";
      if (get("patient.medicalRecordNumber") !== undefined)
        patch["patient.medicalRecordNumber"] =
          get("patient.medicalRecordNumber") ?? "";
      if (get("contact.mobilePhone") !== undefined)
        patch["patient.mobilePhone"] = get("contact.mobilePhone") ?? "";
      if (get("contact.homePhone") !== undefined)
        patch["patient.homePhone"] = get("contact.homePhone") ?? "";
      if (get("contact.workPhone") !== undefined)
        patch["patient.workPhone"] = get("contact.workPhone") ?? "";
      if (get("contact.email") !== undefined)
        patch["patient.email"] = get("contact.email") ?? "";
      if (get("address.street") !== undefined)
        patch["patient.address.street"] = get("address.street") ?? "";
      if (get("address.state") !== undefined)
        patch["patient.address.state"] = get("address.state") ?? "";
      if (get("address.city") !== undefined)
        patch["patient.address.city"] = get("address.city") ?? "";
      if (get("address.zipCode") !== undefined)
        patch["patient.address.zipCode"] = get("address.zipCode") ?? "";
      if (get("caseInfo.externalCaseNumber") !== undefined)
        patch["caseInfo.externalCaseNumber"] =
          get("caseInfo.externalCaseNumber") ?? "";
      if (get("caseInfo.dateReceived") !== undefined)
        patch["caseInfo.dateReceived"] = parseDateString(
          get("caseInfo.dateReceived")
        );
      if (get("caseInfo.dateCollected") !== undefined)
        patch["caseInfo.dateCollected"] = parseDateString(
          get("caseInfo.dateCollected")
        );
      if (get("caseInfo.clinicalHistory") !== undefined)
        patch["caseInfo.clinicalHistory"] =
          get("caseInfo.clinicalHistory") ?? "";
      if (get("caseInfo.indications") !== undefined)
        patch["caseInfo.indications"] = get("caseInfo.indications") ?? "";
      if (get("caseInfo.impressions") !== undefined)
        patch["caseInfo.impressions"] = get("caseInfo.impressions") ?? "";
      if (get("caseInfo.originatingPracticeId") !== undefined)
        patch["caseInfo.originatingPracticeId"] =
          get("caseInfo.originatingPracticeId") ?? "";
      if (get("caseInfo.consultationReason") !== undefined)
        patch["caseInfo.consultationReason"] =
          get("caseInfo.consultationReason") ?? "";

      Object.entries(patch).forEach(([path, value]) => {
        form.setFieldValue(path, value);
      });
    }
  }, [
    form,
    initialCaseErrorReportId,
    initialProposedChanges,
    isEditMode,
    latestFormValues,
    caseData?.specimens,
    grossCodes,
    refetchLabs,
    refetchPhysicians,
    refetchPractices,
    refetchSurgeryCenters,
  ]);

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      const body: Record<string, unknown> = {};

      // Only include a field if it differs from the original value
      const diff = (newVal: unknown, origVal: unknown): unknown | undefined => {
        const n = newVal === null || newVal === undefined ? "" : String(newVal);
        const o =
          origVal === null || origVal === undefined ? "" : String(origVal);
        return n !== o ? newVal : undefined;
      };

      // Patient identity + contact — compare against op (original patient)
      const patientPayload = filterUnchanged({
        firstName: diff(values.patient.firstName, op?.firstName),
        lastName: diff(values.patient.lastName, op?.lastName),
        dateOfBirth: diff(
          formatDateForApi(values.patient.dateOfBirth),
          op?.dateOfBirth
        ),
        sex: diff(values.patient.sex, op?.sex),
        medicalRecordNumber: diff(
          values.patient.medicalRecordNumber,
          op?.medicalRecordNumber
        ),
        mobilePhone: diff(values.patient.mobilePhone, op?.mobilePhone),
        homePhone: diff(values.patient.homePhone, op?.homePhone),
        workPhone: diff(values.patient.workPhone, op?.workPhone),
        email: diff(values.patient.email, op?.email),
      });
      if (Object.keys(patientPayload).length > 0) body.patient = patientPayload;

      // Patient address — compare against op?.address
      const oa = op?.address;
      const addrPayload = filterUnchanged({
        street: diff(values.patient.address.street, oa?.street),
        state: diff(values.patient.address.state, oa?.state),
        city: diff(values.patient.address.city, oa?.city),
        zipCode: diff(values.patient.address.zipCode, oa?.zipCode),
      });
      if (Object.keys(addrPayload).length > 0) body.address = addrPayload;

      // Primary insurance — compare against caseData?.primaryInsurance
      const opi = caseData?.primaryInsurance;
      const priPayload = filterUnchanged({
        policyNumber: diff(
          values.primaryInsurance.policyNumber,
          opi?.policyNumber
        ),
        authorizationNumber: diff(
          values.primaryInsurance.authorizationNumber,
          opi?.authorizationNumber
        ),
        groupNumber: diff(
          values.primaryInsurance.groupNumber,
          opi?.groupNumber
        ),
        groupName: diff(values.primaryInsurance.groupName, opi?.groupName),
        patientRelationToInsured: diff(
          values.primaryInsurance.patientRelationToInsured,
          opi?.patientRelationToInsured
        ),
      });
      const priAddrPayload = filterUnchanged({
        street: diff(
          values.primaryInsurance.address.street,
          opi?.address?.street
        ),
        state: diff(values.primaryInsurance.address.state, opi?.address?.state),
        city: diff(values.primaryInsurance.address.city, opi?.address?.city),
        zipCode: diff(
          values.primaryInsurance.address.zipCode,
          opi?.address?.zipCode
        ),
      });
      const priMerged = {
        ...priPayload,
        ...(Object.keys(priAddrPayload).length > 0
          ? { address: priAddrPayload }
          : {}),
      };
      if (Object.keys(priMerged).length > 0) body.primaryInsurance = priMerged;

      // Secondary insurance — compare against caseData?.secondaryInsurance
      const osi = caseData?.secondaryInsurance;
      const secPayload = filterUnchanged({
        policyNumber: diff(
          values.secondaryInsurance.policyNumber,
          osi?.policyNumber
        ),
        authorizationNumber: diff(
          values.secondaryInsurance.authorizationNumber,
          osi?.authorizationNumber
        ),
        groupNumber: diff(
          values.secondaryInsurance.groupNumber,
          osi?.groupNumber
        ),
        groupName: diff(values.secondaryInsurance.groupName, osi?.groupName),
        patientRelationToInsured: diff(
          values.secondaryInsurance.patientRelationToInsured,
          osi?.patientRelationToInsured
        ),
      });
      const secAddrPayload = filterUnchanged({
        street: diff(
          values.secondaryInsurance.address.street,
          osi?.address?.street
        ),
        state: diff(
          values.secondaryInsurance.address.state,
          osi?.address?.state
        ),
        city: diff(values.secondaryInsurance.address.city, osi?.address?.city),
        zipCode: diff(
          values.secondaryInsurance.address.zipCode,
          osi?.address?.zipCode
        ),
      });
      const secMerged = {
        ...secPayload,
        ...(Object.keys(secAddrPayload).length > 0
          ? { address: secAddrPayload }
          : {}),
      };
      if (Object.keys(secMerged).length > 0)
        body.secondaryInsurance = secMerged;

      // Case info — compare against caseData
      const cd = caseData;
      const casePayloadValues: Record<string, unknown> = {
        externalCaseNumber: diff(
          values.caseInfo.externalCaseNumber,
          cd?.externalCaseNumber
        ),
        dateReceived: diff(
          formatDateForApi(values.caseInfo.dateReceived),
          cd?.dateReceived
        ),
        dateCollected: diff(
          formatDateForApi(values.caseInfo.dateCollected),
          cd?.dateCollected
        ),
        clinicalHistory: diff(
          values.caseInfo.clinicalHistory,
          cd?.clinicalHistoryNotes
        ),
        indications: diff(values.caseInfo.indications, cd?.indications),
        impressions: diff(values.caseInfo.impressions, cd?.impressions),
        ruleOuts: diff(values.caseInfo.ruleOuts, cd?.ruleOuts),
        originatingPracticeId: diff(
          values.caseInfo.originatingPracticeId,
          cd?.originatingPracticeId
        ),
        originatingPhysicianId: diff(
          values.caseInfo.originatingPhysicianId,
          cd?.originatingPhysicianId
        ),
        originatingSurgeryCenterId: diff(
          values.caseInfo.originatingSurgeryCenterId,
          cd?.originatingSurgeryCenterId
        ),
        consultationReason: diff(
          values.caseInfo.consultationReason,
          cd?.consultationReason
        ),
        consultationOriginalDiagnosis: diff(
          values.caseInfo.consultationOriginalDiagnosis,
          cd?.consultationOriginalDiagnosis
        ),
        consultationClinicalContext: diff(
          values.caseInfo.consultationClinicalContext,
          cd?.consultationClinicalContext
        ),
        requestingPathologist: diff(
          values.caseInfo.requestingPathologist,
          cd?.requestingPathologist
        ),
      };
      if (!isTcOnly) {
        Object.assign(casePayloadValues, {
          shippingPathologist: diff(
            values.caseInfo.shippingPathologist,
            cd?.shippingPathologist
          ),
          shippingStreet: diff(
            values.caseInfo.shippingStreet,
            cd?.shippingStreet
          ),
          shippingState: diff(values.caseInfo.shippingState, cd?.shippingState),
          shippingCity: diff(values.caseInfo.shippingCity, cd?.shippingCity),
          shippingZipCode: diff(
            values.caseInfo.shippingZipCode,
            cd?.shippingZipCode
          ),
        });
      }
      const casePayload = filterUnchanged(casePayloadValues);
      const clientIdentityPayload = {
        sendingPracticeId: diffClearableId(
          values.caseInfo.sendingPracticeId,
          cd?.sendingPracticeId
        ),
        physicianId: diffClearableId(
          values.caseInfo.physicianId,
          cd?.physicianId
        ),
        surgeryCenterLabId: diffClearableId(
          values.caseInfo.surgeryCenterLabId,
          cd?.surgeryCenterLabId
        ),
        submittingLabId: diffClearableId(
          values.caseInfo.submittingLabId,
          cd?.submittingLabId
        ),
      };
      Object.entries(clientIdentityPayload).forEach(([key, value]) => {
        if (value !== undefined) casePayload[key] = value;
      });
      const hasClientIdentityChanges = Object.values(
        clientIdentityPayload
      ).some((value) => value !== undefined);
      if (Object.keys(casePayload).length > 0) body.caseInfo = casePayload;
      if (hasClientIdentityChanges) {
        const previewedSequence = parseCaseNumber(
          clientIdentityPreview.preview?.proposed.caseNumber ?? ""
        )?.sequence;
        body.clientIdentity =
          clientIdentityDirective.mode === "apply" && previewedSequence
            ? {
                ...clientIdentityDirective,
                prefixCode: clientIdentityPreview.preview?.proposed.prefixCode,
                sequence: previewedSequence,
              }
            : clientIdentityDirective;
      }

      const prefixSelection = values.prefixReassignment;
      const prefixContext = signedPrefixContextQuery.data;
      const selectedPrefix = prefixContext?.availablePrefixes.find(
        (prefix) => prefix.id === prefixSelection.targetPrefixId
      );
      if (prefixSelection.targetPrefixId && prefixContext && selectedPrefix) {
        body.prefixReassignment = {
          targetPrefixId: selectedPrefix.id,
          expectedCurrentCaseNumber: prefixContext.currentIdentity.caseNumber,
          expectedCurrentPrefixId: prefixContext.currentIdentity.prefix.id,
          ...(selectedPrefix.requiresCaseNumberRemainder
            ? {
                caseNumberRemainder: prefixSelection.caseNumberRemainder
                  .trim()
                  .toUpperCase(),
              }
            : {}),
        };
      }
      if (values.reportTimezoneCorrection) {
        body.reportTimezoneCorrection = true;
      }

      if (values.note) body.note = values.note;

      // Guard: nothing changed (allow save if only the error link changed in edit mode)
      const errorLinkChanged =
        isEditMode && linkedErrorId !== (initialCaseErrorReportId ?? null);
      if (
        Object.keys(body).length === 0 &&
        specimenChanges.length === 0 &&
        !errorLinkChanged
      ) {
        notifications.show({
          title: "Nothing to Save",
          message:
            "No fields were changed. Edit the values you want to update and try again.",
          color: "yellow",
        });
        return;
      }

      if (
        hasClientIdentityChanges &&
        clientIdentityDirective.mode !== "keep" &&
        !clientIdentityPreview.preview
      ) {
        notifications.show({
          title: "Identity preview still loading",
          message:
            "Wait for the case-number impact to finish loading, then save again.",
          color: "yellow",
        });
        return;
      }

      if (
        hasClientIdentityChanges &&
        clientIdentityDirective.mode === "manual"
      ) {
        const sequenceError =
          clientIdentityDirective.sequence === null ||
          clientIdentityDirective.sequence === undefined
            ? null
            : validateCaseNumberSequence(clientIdentityDirective.sequence);
        const resolvedPrefix =
          clientIdentityPreview.preview?.proposed.prefixCode;
        const overrideNeedsAcknowledgement =
          !!resolvedPrefix &&
          clientIdentityDirective.prefixCode !== resolvedPrefix &&
          !clientIdentityDirective.acknowledgedOverride;
        if (
          !clientIdentityDirective.prefixCode ||
          sequenceError ||
          overrideNeedsAcknowledgement
        ) {
          notifications.show({
            title: "Check the manual case number",
            message:
              sequenceError ||
              (!clientIdentityDirective.prefixCode
                ? "Choose a prefix before saving."
                : "Acknowledge that the manual prefix overrides the computed prefix."),
            color: "yellow",
          });
          return;
        }
      }

      if (isCorrectionAmendmentMode) {
        const directBody: DirectCorrectionAmendmentBody = {
          ...(body as any),
          reason: values.reason,
          ...(specimenChanges.length > 0 ? { specimens: specimenChanges } : {}),
        };
        if (specimenChanges.length > 0 || values.reportTimezoneCorrection) {
          const previewResult = await directPreviewMutation.mutateAsync(
            directBody
          );
          setPendingCorrectionBody({
            ...directBody,
            correctionId: previewResult.correctionId,
            correctionTimestamp: previewResult.correctionTimestamp,
          });
          setCorrectionPreview(previewResult);
          setCorrectionScreen("preview");
          return;
        }
        await directMutation.mutateAsync(directBody);
        onClose();
      } else if (isReviewMode && editingCorrectionRequestId) {
        await actMutation.mutateAsync({
          requestId: editingCorrectionRequestId,
          action: "modify_and_apply",
          appliedChanges: body as Record<string, any>,
          note: values.reason,
          caseId,
        });
        onClose();
      } else if (isEditMode && editingCorrectionRequestId) {
        await updateMutation.mutateAsync({
          requestId: editingCorrectionRequestId,
          caseId,
          body: {
            ...(body as Record<string, any>),
            caseErrorReportId: linkedErrorId,
          },
        });
        onClose();
      } else {
        const response = await mutation.mutateAsync({
          ...(body as any),
          ...(linkedErrorId ? { caseErrorReportId: linkedErrorId } : {}),
        });
        if ("identityChange" in response && response.identityChange) {
          const staleMaterials = response.identityChange.staleMaterialTypes;
          notifications.show({
            title: "Case number changed — check printed materials",
            message:
              staleMaterials.length > 0
                ? `Reprint ${staleMaterials.join(" and ")} with ${
                    response.identityChange.newCaseNumber
                  }.`
                : "No previously printed cassettes or slides were detected.",
            color: "orange",
            autoClose: false,
          });
        }
        onClose();
      }
    } catch (err: any) {
      const fieldErrors = err?.response?.data?.fieldErrors;
      if (fieldErrors) {
        Object.entries(fieldErrors).forEach(([field, message]) => {
          form.setFieldError(field, message as string);
        });
        return;
      }
      // Server-side failures are already reported by the mutation hooks. Anything
      // without a response is a client-side fault — surface it instead of leaving
      // the user staring at a button that appears to do nothing.
      if (!err?.response) {
        notifications.show({
          title: "Changes could not be submitted",
          message:
            "Something went wrong before the request was sent. Reopen the modal and try again.",
          color: "red",
        });
      }
    }
  });

  const handleAcknowledgeCorrectionPreview = useCallback(
    async (contentVersion: ReportContentVersion) => {
      if (!pendingCorrectionBody) {
        throw new Error(
          "The correction preview expired. Return to the correction and review it again."
        );
      }
      return patientInfoService.acknowledgeDirectCorrectionPreview(
        caseId,
        pendingCorrectionBody,
        contentVersion
      );
    },
    [caseId, pendingCorrectionBody]
  );

  const handleApplyPreviewedCorrection = useCallback(
    async (previewEvidence?: ReportPreviewEvidence) => {
      if (!pendingCorrectionBody || !previewEvidence) return;
      await directMutation.mutateAsync({
        ...pendingCorrectionBody,
        previewEvidence,
      });
      preserveCorrectionDraftOnNextEnterRef.current = false;
      setCorrectionScreen("details");
      setPendingCorrectionBody(null);
      setCorrectionPreview(null);
      onClose();
    },
    [directMutation, onClose, pendingCorrectionBody]
  );

  const handleCorrectionPreviewContentChanged = useCallback(async () => {
    if (!pendingCorrectionBody) return;
    const previewResult = await directPreviewMutation.mutateAsync(
      pendingCorrectionBody
    );
    setPendingCorrectionBody({
      ...pendingCorrectionBody,
      correctionId: previewResult.correctionId,
      correctionTimestamp: previewResult.correctionTimestamp,
    });
    setCorrectionPreview(previewResult);
  }, [directPreviewMutation, pendingCorrectionBody]);

  // ── Derived ────────────────────────────────────────────────────────────────

  const fc = fieldCompletion;
  const signedPrefixSelection = useMemo(
    () => form.values.prefixReassignment,
    [form.values.prefixReassignment]
  );
  const signedPrefixErrors = useMemo(
    () =>
      ({
        targetPrefixId: form.errors["prefixReassignment.targetPrefixId"] as
          | string
          | undefined,
        caseNumberRemainder: form.errors[
          "prefixReassignment.caseNumberRemainder"
        ] as string | undefined,
      } satisfies SignedCorrectionPrefixErrors),
    [form.errors]
  );
  const modalTitle = useMemo(() => {
    if (correctionScreen === "grossing") return "Correct Grossing Details";
    if (isCorrectionAmendmentMode) return "Create Report Correction";
    if (isReviewMode) return "Review Correction Request";
    if (isEditMode) return "Edit Correction Request";
    return "Update Patient Information";
  }, [correctionScreen, isCorrectionAmendmentMode, isEditMode, isReviewMode]);
  const modalSize = useMemo(
    () => (correctionScreen === "grossing" ? "calc(100vw - 64px)" : "xl"),
    [correctionScreen]
  );
  const correctedPreviewData = useMemo(
    () =>
      correctionPreview
        ? {
            ...correctionPreview.previewData,
            review: correctionPreview.review,
          }
        : undefined,
    [correctionPreview]
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Modal.Root
        key={correctionScreen === "preview" ? "correction-preview" : "editor"}
        opened={opened && correctionScreen !== "preview"}
        onClose={onClose}
        transitionProps={{ onEnter: handleModalEnter }}
        size={modalSize}
        closeOnClickOutside={false}
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
          },
          header: { flexShrink: 0 },
          body: { overflowY: "auto", flex: 1, minHeight: 0 },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>{modalTitle}</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>

          <Modal.Body>
            <form
              id="update-patient-info-form"
              onSubmit={handleSubmit}
              style={{
                display: correctionScreen === "details" ? undefined : "none",
              }}
            >
              <Stack gap="xs">
                <Accordion
                  multiple
                  value={openedSections}
                  onChange={setOpenedSections}
                  variant="separated"
                >
                  {/* ── Patient Information ── */}
                  <Accordion.Item value="patient">
                    <Accordion.Control>
                      <Group
                        justify="space-between"
                        style={{ flex: 1 }}
                        pr="md"
                      >
                        <Title order={4}>Patient Information</Title>
                        <Badge
                          color={fc.patient.requiredComplete ? "green" : "gray"}
                          variant="light"
                        >
                          {fc.patient.completed}/{fc.patient.total} fields
                        </Badge>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="md">
                        {/* Identity */}
                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                          <CertaintyTextInput
                            label="First Name"
                            placeholder="First name"
                            required={!!op?.firstName}
                            description={hint("patient.firstName")}
                            {...form.getInputProps("patient.firstName")}
                          />
                          <CertaintyTextInput
                            label="Last Name"
                            placeholder="Last name"
                            required={!!op?.lastName}
                            description={hint("patient.lastName")}
                            {...form.getInputProps("patient.lastName")}
                          />
                        </SimpleGrid>

                        <SimpleGrid cols={{ base: 1, sm: 3 }}>
                          <CertaintyDateInput
                            label="Date of Birth"
                            placeholder="MM/DD/YYYY"
                            required={!!op?.dateOfBirth}
                            description={hint("patient.dateOfBirth")}
                            {...form.getInputProps("patient.dateOfBirth")}
                          />
                          <CertaintySelect
                            label="Sex"
                            placeholder="Select sex"
                            required={!!op?.sex}
                            description={hint("patient.sex")}
                            data={[
                              { value: "M", label: "Male" },
                              { value: "F", label: "Female" },
                              { value: "Other", label: "Other" },
                            ]}
                            {...form.getInputProps("patient.sex")}
                          />
                          <CertaintyTextInput
                            label="Medical Record Number"
                            placeholder="MRN"
                            required={!!op?.medicalRecordNumber}
                            description={hint("patient.medicalRecordNumber")}
                            {...form.getInputProps(
                              "patient.medicalRecordNumber"
                            )}
                          />
                        </SimpleGrid>

                        {/* Contact */}
                        <Title order={5} mt="xs">
                          Contact Information
                        </Title>
                        <SimpleGrid cols={{ base: 1, sm: 3 }}>
                          <CertaintyTextInput
                            label="Mobile Phone"
                            placeholder="(555) 555-5555"
                            description={hint("patient.mobilePhone")}
                            {...form.getInputProps("patient.mobilePhone")}
                          />
                          <CertaintyTextInput
                            label="Home Phone"
                            placeholder="(555) 555-5555"
                            description={hint("patient.homePhone")}
                            {...form.getInputProps("patient.homePhone")}
                          />
                          <CertaintyTextInput
                            label="Work Phone"
                            placeholder="(555) 555-5555"
                            description={hint("patient.workPhone")}
                            {...form.getInputProps("patient.workPhone")}
                          />
                        </SimpleGrid>
                        <CertaintyTextInput
                          label="Email"
                          placeholder="patient@example.com"
                          type="email"
                          description={hint("patient.email")}
                          {...form.getInputProps("patient.email")}
                        />

                        {/* Address */}
                        <Title order={5} mt="xs">
                          Address
                        </Title>
                        <CertaintyTextInput
                          label="Street Address"
                          placeholder="123 Main St"
                          description={hint("patient.address.street")}
                          {...form.getInputProps("patient.address.street")}
                        />
                        <SimpleGrid cols={{ base: 1, sm: 3 }}>
                          <CertaintySelect
                            label="State"
                            placeholder="Select state"
                            data={sanitizeOptions(states)}
                            searchable
                            description={hint("patient.address.state")}
                            {...form.getInputProps("patient.address.state")}
                            onChange={(value) => {
                              form.setFieldValue(
                                "patient.address.state",
                                value
                              );
                              form.setFieldValue("patient.address.city", "");
                            }}
                          />
                          <CertaintySelect
                            label="City"
                            placeholder={
                              patientState
                                ? "Select city"
                                : "Select state first"
                            }
                            data={sanitizeOptions(patientCities)}
                            searchable
                            disabled={!patientState}
                            description={hint("patient.address.city")}
                            {...form.getInputProps("patient.address.city")}
                          />
                          <CertaintyTextInput
                            label="ZIP Code"
                            placeholder="12345"
                            description={hint("patient.address.zipCode")}
                            {...form.getInputProps("patient.address.zipCode")}
                          />
                        </SimpleGrid>
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>

                  {/* ── Primary Insurance ── */}
                  <Accordion.Item value="primaryInsurance">
                    <Accordion.Control>
                      <Group
                        justify="space-between"
                        style={{ flex: 1 }}
                        pr="md"
                      >
                        <Title order={4}>Primary Insurance</Title>
                        <Badge
                          color={
                            fc.primaryInsurance.recommendedComplete
                              ? "green"
                              : "yellow"
                          }
                          variant="light"
                        >
                          {fc.primaryInsurance.recommendedComplete
                            ? `${fc.primaryInsurance.completed}/${fc.primaryInsurance.total} fields`
                            : "Recommended"}
                        </Badge>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="sm">
                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                          <CertaintyTextInput
                            label="Policy Number"
                            placeholder="Policy #"
                            description={
                              hint("primaryInsurance.policyNumber") ??
                              "Recommended — will be flagged if missing"
                            }
                            showReviewBadge
                            reviewTooltip="Verify insurance policy number"
                            {...form.getInputProps(
                              "primaryInsurance.policyNumber"
                            )}
                          />
                          <CertaintyTextInput
                            label="Authorization Number"
                            placeholder="Auth #"
                            description={hint(
                              "primaryInsurance.authorizationNumber"
                            )}
                            {...form.getInputProps(
                              "primaryInsurance.authorizationNumber"
                            )}
                          />
                          <CertaintyTextInput
                            label="Group Number"
                            placeholder="Group #"
                            showReviewBadge
                            reviewTooltip="Verify insurance group number"
                            description={hint("primaryInsurance.groupNumber")}
                            {...form.getInputProps(
                              "primaryInsurance.groupNumber"
                            )}
                          />
                          <CertaintyTextInput
                            label="Group Name"
                            placeholder="Group name"
                            showReviewBadge
                            reviewTooltip="Verify insurance group name"
                            description={hint("primaryInsurance.groupName")}
                            {...form.getInputProps(
                              "primaryInsurance.groupName"
                            )}
                          />
                          <CertaintySelect
                            label="Patient Relation to Insured"
                            placeholder="Select relation"
                            description={hint(
                              "primaryInsurance.patientRelationToInsured"
                            )}
                            data={[
                              { value: "Self", label: "Self" },
                              { value: "Spouse", label: "Spouse" },
                              { value: "Child", label: "Child" },
                              { value: "Other", label: "Other" },
                            ]}
                            {...form.getInputProps(
                              "primaryInsurance.patientRelationToInsured"
                            )}
                          />
                        </SimpleGrid>

                        <Title order={6} c="dimmed" mt="xs">
                          Insurance Address
                        </Title>
                        <CertaintyTextInput
                          label="Street Address"
                          placeholder="123 Main St"
                          {...form.getInputProps(
                            "primaryInsurance.address.street"
                          )}
                        />
                        <SimpleGrid cols={{ base: 1, sm: 3 }}>
                          <CertaintySelect
                            label="State"
                            placeholder="Select state"
                            data={sanitizeOptions(states)}
                            searchable
                            {...form.getInputProps(
                              "primaryInsurance.address.state"
                            )}
                            onChange={(value) => {
                              form.setFieldValue(
                                "primaryInsurance.address.state",
                                value
                              );
                              form.setFieldValue(
                                "primaryInsurance.address.city",
                                ""
                              );
                            }}
                          />
                          <CertaintySelect
                            label="City"
                            placeholder={
                              priInsState ? "Select city" : "Select state first"
                            }
                            data={sanitizeOptions(priInsCities)}
                            searchable
                            disabled={!priInsState}
                            {...form.getInputProps(
                              "primaryInsurance.address.city"
                            )}
                          />
                          <CertaintyTextInput
                            label="ZIP Code"
                            placeholder="12345"
                            {...form.getInputProps(
                              "primaryInsurance.address.zipCode"
                            )}
                          />
                        </SimpleGrid>
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>

                  {/* ── Secondary Insurance (collapsed by default) ── */}
                  <Accordion.Item value="secondaryInsurance">
                    <Accordion.Control>
                      <Group
                        justify="space-between"
                        style={{ flex: 1 }}
                        pr="md"
                      >
                        <Title order={4}>Secondary Insurance</Title>
                        <Badge
                          color={
                            fc.secondaryInsurance.completed > 0
                              ? "green"
                              : "gray"
                          }
                          variant="light"
                        >
                          {fc.secondaryInsurance.completed}/
                          {fc.secondaryInsurance.total} fields
                        </Badge>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="sm">
                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                          <CertaintyTextInput
                            label="Policy Number"
                            placeholder="Policy #"
                            description={hint(
                              "secondaryInsurance.policyNumber"
                            )}
                            {...form.getInputProps(
                              "secondaryInsurance.policyNumber"
                            )}
                          />
                          <CertaintyTextInput
                            label="Authorization Number"
                            placeholder="Auth #"
                            description={hint(
                              "secondaryInsurance.authorizationNumber"
                            )}
                            {...form.getInputProps(
                              "secondaryInsurance.authorizationNumber"
                            )}
                          />
                          <CertaintyTextInput
                            label="Group Number"
                            placeholder="Group #"
                            description={hint("secondaryInsurance.groupNumber")}
                            {...form.getInputProps(
                              "secondaryInsurance.groupNumber"
                            )}
                          />
                          <CertaintyTextInput
                            label="Group Name"
                            placeholder="Group name"
                            description={hint("secondaryInsurance.groupName")}
                            {...form.getInputProps(
                              "secondaryInsurance.groupName"
                            )}
                          />
                          <CertaintySelect
                            label="Patient Relation to Insured"
                            placeholder="Select relation"
                            description={hint(
                              "secondaryInsurance.patientRelationToInsured"
                            )}
                            data={[
                              { value: "Self", label: "Self" },
                              { value: "Spouse", label: "Spouse" },
                              { value: "Child", label: "Child" },
                              { value: "Other", label: "Other" },
                            ]}
                            {...form.getInputProps(
                              "secondaryInsurance.patientRelationToInsured"
                            )}
                          />
                        </SimpleGrid>

                        <Title order={6} c="dimmed" mt="xs">
                          Insurance Address
                        </Title>
                        <CertaintyTextInput
                          label="Street Address"
                          placeholder="123 Main St"
                          {...form.getInputProps(
                            "secondaryInsurance.address.street"
                          )}
                        />
                        <SimpleGrid cols={{ base: 1, sm: 3 }}>
                          <CertaintySelect
                            label="State"
                            placeholder="Select state"
                            data={sanitizeOptions(states)}
                            searchable
                            {...form.getInputProps(
                              "secondaryInsurance.address.state"
                            )}
                            onChange={(value) => {
                              form.setFieldValue(
                                "secondaryInsurance.address.state",
                                value
                              );
                              form.setFieldValue(
                                "secondaryInsurance.address.city",
                                ""
                              );
                            }}
                          />
                          <CertaintySelect
                            label="City"
                            placeholder={
                              secInsState ? "Select city" : "Select state first"
                            }
                            data={sanitizeOptions(secInsCities)}
                            searchable
                            disabled={!secInsState}
                            {...form.getInputProps(
                              "secondaryInsurance.address.city"
                            )}
                          />
                          <CertaintyTextInput
                            label="ZIP Code"
                            placeholder="12345"
                            {...form.getInputProps(
                              "secondaryInsurance.address.zipCode"
                            )}
                          />
                        </SimpleGrid>
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>

                  {/* ── Client & Provider ── */}
                  <Accordion.Item value="clientProvider">
                    <Accordion.Control>
                      <Title order={4}>Client &amp; Provider</Title>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="sm">
                        {clientProviderFieldsLocked && (
                          <Alert
                            color="gray"
                            variant="light"
                            title="Client identity is locked after sign-off"
                            role="status"
                          >
                            The practice, physician, surgery center, and
                            submitting lab are part of the signed report
                            identity and cannot be changed here.
                          </Alert>
                        )}
                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                          <Select
                            label="Sending practice"
                            placeholder="Select a practice"
                            description={hint("caseInfo.sendingPracticeId")}
                            data={sanitizeOptions(practices)}
                            value={form.values.caseInfo.sendingPracticeId}
                            onChange={handleSendingPracticeChange}
                            clearable
                            searchable
                            disabled={clientProviderFieldsLocked}
                            nothingFoundMessage={
                              practicesLoading
                                ? "Loading practices…"
                                : practicesFailed
                                ? "Practices could not be loaded"
                                : practices.length === 0
                                ? "No active practices are configured"
                                : "No matching practice"
                            }
                            comboboxProps={{
                              withinPortal: true,
                              position: "bottom-start",
                              offset: 4,
                            }}
                          />
                          <Select
                            label="Physician"
                            placeholder="Select a physician"
                            description={hint("caseInfo.physicianId")}
                            data={sanitizeOptions(physicians)}
                            value={form.values.caseInfo.physicianId}
                            onChange={handlePhysicianChange}
                            clearable
                            searchable
                            disabled={clientProviderFieldsLocked}
                            nothingFoundMessage={
                              physiciansLoading
                                ? "Loading physicians…"
                                : physiciansFailed
                                ? "Physicians could not be loaded"
                                : physicians.length === 0
                                ? "No active physicians are configured"
                                : "No matching physician"
                            }
                            comboboxProps={{
                              withinPortal: true,
                              position: "bottom-start",
                              offset: 4,
                            }}
                          />
                          <Select
                            label="Surgery center"
                            placeholder="Select a surgery center"
                            description={hint("caseInfo.surgeryCenterLabId")}
                            data={sanitizeOptions(originatingSurgeryCenters)}
                            value={form.values.caseInfo.surgeryCenterLabId}
                            onChange={handleSurgeryCenterChange}
                            clearable
                            searchable
                            disabled={clientProviderFieldsLocked}
                            nothingFoundMessage={
                              surgeryCentersLoading
                                ? "Loading surgery centers…"
                                : surgeryCentersFailed
                                ? "Surgery centers could not be loaded"
                                : originatingSurgeryCenters.length === 0
                                ? "No active surgery centers are configured"
                                : "No matching surgery center"
                            }
                            comboboxProps={{
                              withinPortal: true,
                              position: "bottom-start",
                              offset: 4,
                            }}
                          />
                          <Select
                            label="Submitting lab"
                            placeholder="Select a lab"
                            description={hint("caseInfo.submittingLabId")}
                            data={sanitizeOptions(labs)}
                            value={form.values.caseInfo.submittingLabId}
                            onChange={handleSubmittingLabChange}
                            clearable
                            searchable
                            disabled={clientProviderFieldsLocked}
                            nothingFoundMessage={
                              labsLoading
                                ? "Loading labs…"
                                : labsFailed
                                ? "Labs could not be loaded"
                                : labs.length === 0
                                ? "No active submitting labs are configured"
                                : "No matching lab"
                            }
                            comboboxProps={{
                              withinPortal: true,
                              position: "bottom-start",
                              offset: 4,
                            }}
                          />
                        </SimpleGrid>

                        {!isSignedOff &&
                          !isTcOnly &&
                          clientIdentityChanged &&
                          clientIdentityPreview.error && (
                            <Alert
                              color="red"
                              variant="light"
                              title="Unable to preview the case identity"
                              role="alert"
                            >
                              The client details cannot be resolved right now.
                              Keep the current case number or try again before
                              choosing a new one.
                            </Alert>
                          )}

                        {!isSignedOff && !isTcOnly && clientIdentityChanged && (
                          <ClientIdentityImpactPanel
                            preview={clientIdentityPreview.preview}
                            isLoading={clientIdentityPreview.isLoading}
                            directive={clientIdentityDirective}
                            onDirectiveChange={
                              handleClientIdentityDirectiveChange
                            }
                          />
                        )}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>

                  {/* ── Case Information ── */}
                  <Accordion.Item value="caseInfo">
                    <Accordion.Control>
                      <Group
                        justify="space-between"
                        style={{ flex: 1 }}
                        pr="md"
                      >
                        <Title order={4}>Case Information</Title>
                        <Badge
                          color={fc.caseInfo.completed > 0 ? "green" : "gray"}
                          variant="light"
                        >
                          {fc.caseInfo.completed}/{fc.caseInfo.total} fields
                        </Badge>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="sm">
                        <SimpleGrid cols={{ base: 1, sm: 2 }}>
                          <CertaintyTextInput
                            label="External Case Number"
                            description={
                              hint("caseInfo.externalCaseNumber") ??
                              "Case/specimen ID from the originating facility"
                            }
                            placeholder="e.g., SC-2024-1234"
                            {...form.getInputProps(
                              "caseInfo.externalCaseNumber"
                            )}
                          />
                          <CertaintyTextInput
                            label="Rule Outs"
                            description={
                              hint("caseInfo.ruleOuts") ??
                              "Differential diagnoses (comma separated)"
                            }
                            placeholder="e.g., Crohn's disease, IBD"
                            {...form.getInputProps("caseInfo.ruleOuts")}
                          />
                          <CertaintyDateInput
                            label="Date Received"
                            placeholder="MM/DD/YYYY"
                            description={
                              hint("caseInfo.dateReceived") ??
                              "When lab received the specimens"
                            }
                            showReviewBadge
                            reviewTooltip="Verify date lab received the specimens"
                            {...form.getInputProps("caseInfo.dateReceived")}
                          />
                          <CertaintyDateInput
                            label="Date Collected (Date of Service)"
                            placeholder="MM/DD/YYYY"
                            description={
                              hint("caseInfo.dateCollected") ??
                              "When the procedure was performed"
                            }
                            showReviewBadge
                            reviewTooltip="Verify date of service / procedure date"
                            {...form.getInputProps("caseInfo.dateCollected")}
                          />
                        </SimpleGrid>

                        {/* Rich-text fields — use value/onChange, not getInputProps spread */}
                        <CertaintyRichText
                          label="Clinical History"
                          placeholder="Past medical history"
                          showReviewBadge
                          reviewTooltip="Review clinical history carefully"
                          value={form.values.caseInfo.clinicalHistory}
                          onChange={(val) =>
                            form.setFieldValue("caseInfo.clinicalHistory", val)
                          }
                        />
                        <PatientInfoFieldHint
                          formPath="caseInfo.clinicalHistory"
                          changes={
                            isReviewMode ? initialProposedChanges : undefined
                          }
                        />
                        <CertaintyRichText
                          label="Indications"
                          placeholder="Reason for procedure"
                          showReviewBadge
                          reviewTooltip="Review indications carefully"
                          value={form.values.caseInfo.indications}
                          onChange={(val) =>
                            form.setFieldValue("caseInfo.indications", val)
                          }
                        />
                        <PatientInfoFieldHint
                          formPath="caseInfo.indications"
                          changes={
                            isReviewMode ? initialProposedChanges : undefined
                          }
                        />
                        <CertaintyRichText
                          label="Impressions"
                          placeholder="Procedural findings"
                          showReviewBadge
                          reviewTooltip="Review impressions carefully"
                          value={form.values.caseInfo.impressions}
                          onChange={(val) =>
                            form.setFieldValue("caseInfo.impressions", val)
                          }
                        />
                        <PatientInfoFieldHint
                          formPath="caseInfo.impressions"
                          changes={
                            isReviewMode ? initialProposedChanges : undefined
                          }
                        />

                        {/* Originating facility — pass-through / consultation / TC */}
                        {showOriginatingFacility && (
                          <>
                            <Title order={5} mt="xs">
                              Originating Facility
                            </Title>
                            <SimpleGrid cols={{ base: 1, sm: 2 }}>
                              <CertaintySelect
                                label="Originating Practice"
                                placeholder="Select originating practice"
                                description="The practice that sent the case"
                                data={sanitizeOptions(practices)}
                                searchable
                                {...form.getInputProps(
                                  "caseInfo.originatingPracticeId"
                                )}
                                onChange={(value) => {
                                  form.setFieldValue(
                                    "caseInfo.originatingPracticeId",
                                    value
                                  );
                                }}
                              />
                              <CertaintySelect
                                label="Originating Physician"
                                placeholder="Select originating physician"
                                description="The physician from the originating facility"
                                data={sanitizeOptions(physicians)}
                                searchable
                                {...form.getInputProps(
                                  "caseInfo.originatingPhysicianId"
                                )}
                              />
                              <CertaintySelect
                                label="Originating Surgery Center"
                                placeholder="Select surgery center"
                                description="The surgery center from the originating facility"
                                data={sanitizeOptions(
                                  originatingSurgeryCenters
                                )}
                                searchable
                                {...form.getInputProps(
                                  "caseInfo.originatingSurgeryCenterId"
                                )}
                              />
                            </SimpleGrid>
                          </>
                        )}

                        {/* Consultation fields */}
                        {showConsultationFields && (
                          <>
                            <Title order={5} mt="xs">
                              Consultation Details
                            </Title>
                            <CertaintyTextarea
                              label="Consultation Reason"
                              placeholder="Reason for consultation"
                              required
                              autosize
                              minRows={2}
                              maxRows={4}
                              description={hint("caseInfo.consultationReason")}
                              {...form.getInputProps(
                                "caseInfo.consultationReason"
                              )}
                            />
                            <CertaintyRichText
                              label="Original Diagnosis"
                              placeholder="Original diagnosis from referring lab (optional)"
                              value={
                                form.values.caseInfo
                                  .consultationOriginalDiagnosis
                              }
                              onChange={handleOriginalDiagnosisChange}
                            />
                            <PatientInfoFieldHint
                              formPath="caseInfo.consultationOriginalDiagnosis"
                              changes={
                                isReviewMode
                                  ? initialProposedChanges
                                  : undefined
                              }
                            />
                            <CertaintyTextarea
                              label="Clinical Context"
                              placeholder="Clinical context specific to this consultation (optional)"
                              autosize
                              minRows={2}
                              maxRows={4}
                              description={hint(
                                "caseInfo.consultationClinicalContext"
                              )}
                              {...form.getInputProps(
                                "caseInfo.consultationClinicalContext"
                              )}
                            />
                            <CertaintyTextInput
                              label="Requesting Pathologist"
                              placeholder="Pathologist at sending lab requesting consultation (optional)"
                              description={hint(
                                "caseInfo.requestingPathologist"
                              )}
                              {...form.getInputProps(
                                "caseInfo.requestingPathologist"
                              )}
                            />
                          </>
                        )}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>

                {isCorrectionAmendmentMode && canReassignPrefix && (
                  <SignedCorrectionPrefixFields
                    context={signedPrefixContextQuery.data}
                    isLoading={signedPrefixContextQuery.isLoading}
                    isError={signedPrefixContextQuery.isError}
                    disabled={directMutation.isPending}
                    selection={signedPrefixSelection}
                    errors={signedPrefixErrors}
                    onChange={handleSignedPrefixSelectionChange}
                  />
                )}

                {isCorrectionAmendmentMode &&
                  reportTimezoneCorrectionContext?.eligible && (
                    <Alert
                      color="orange"
                      variant="light"
                      title="Affected report time zone"
                      role="status"
                    >
                      <Checkbox
                        label="Update report time zone"
                        description={`Create a corrected report using ${reportTimezoneCorrectionContext.timeZone} for the reported date and sign-out time.`}
                        {...form.getInputProps("reportTimezoneCorrection", {
                          type: "checkbox",
                        })}
                      />
                      <Stack gap={2} mt="xs">
                        {Object.entries(
                          reportTimezoneCorrectionContext.changes
                        ).map(([fieldPath, change]) => (
                          <Text key={fieldPath} size="xs">
                            {change.label || fieldPath}: {String(change.before)}
                            {" → "}
                            {String(change.after)}
                          </Text>
                        ))}
                      </Stack>
                    </Alert>
                  )}

                {isCorrectionAmendmentMode &&
                  reportTimezoneCorrectionQuery.isError && (
                    <Alert
                      color="red"
                      variant="light"
                      title="Report time zone status unavailable"
                      role="alert"
                    >
                      Close and reopen this correction before changing report
                      timestamps.
                    </Alert>
                  )}

                {/* ── Reason / Note ── */}
                <Box px={4}>
                  {isCorrectionUX ? (
                    <>
                      <CertaintyRichText
                        label="Reason for Modification"
                        placeholder="Explain why you are modifying these values..."
                        value={form.values.reason}
                        onChange={handleReasonChange}
                      />
                      {form.errors.reason && (
                        <Alert
                          color="red"
                          variant="light"
                          role="alert"
                          mt="xs"
                          title="Reason required"
                        >
                          {form.errors.reason}
                        </Alert>
                      )}
                      <Alert color="blue" variant="light" mt="xs">
                        These changes will be applied immediately and a
                        corrected version of this report will be generated. The
                        report will be marked as a correction and the updated
                        information will be reflected in the final document.
                      </Alert>
                    </>
                  ) : (
                    <>
                      <CertaintyRichText
                        label="Note (optional)"
                        placeholder="Add a note about these changes..."
                        value={form.values.note}
                        onChange={(val) => form.setFieldValue("note", val)}
                      />
                      {isEditMode ? (
                        <>
                          <Alert color="orange" variant="light" mt="xs">
                            You are editing an existing correction request. Your
                            updated changes will replace the previous proposed
                            values.
                          </Alert>
                          {showLinkError && (
                            <Box mt="sm">
                              <Text
                                size="xs"
                                c="dimmed"
                                tt="uppercase"
                                fw={600}
                                mb={4}
                              >
                                Link to Error Report (Optional)
                              </Text>
                              <Select
                                placeholder={
                                  pendingErrorOptions.length === 0
                                    ? "No pending errors for this case"
                                    : "Select an error this correction resolves..."
                                }
                                data={pendingErrorOptions}
                                value={linkedErrorId}
                                onChange={handleLinkedErrorChange}
                                clearable
                                searchable
                                disabled={pendingErrorOptions.length === 0}
                                nothingFoundMessage="No results"
                              />
                            </Box>
                          )}
                        </>
                      ) : isSignedOff ? (
                        <>
                          <Alert color="blue" variant="light" mt="xs">
                            This case is signed off. Your changes will be
                            submitted as a correction request for pathologist
                            review.
                          </Alert>
                          {showLinkError && (
                            <Box mt="sm">
                              <Text
                                size="xs"
                                c="dimmed"
                                tt="uppercase"
                                fw={600}
                                mb={4}
                              >
                                Link to Error Report (Optional)
                              </Text>
                              <Select
                                placeholder={
                                  pendingErrorOptions.length === 0
                                    ? "No pending errors for this case"
                                    : "Select an error this correction resolves..."
                                }
                                data={pendingErrorOptions}
                                value={linkedErrorId}
                                onChange={handleLinkedErrorChange}
                                clearable
                                searchable
                                disabled={pendingErrorOptions.length === 0}
                                nothingFoundMessage="No results"
                              />
                            </Box>
                          )}
                        </>
                      ) : (
                        <Text size="xs" c="dimmed" mt="xs">
                          Changes will be applied immediately.
                        </Text>
                      )}
                    </>
                  )}
                </Box>
              </Stack>
            </form>
            {correctionScreen === "grossing" && (
              <DirectCorrectionGrossingScreen
                caseData={caseData}
                grossingDataMap={grossingDataMap}
                changedSpecimenCount={specimenChanges.length}
                onGrossingChange={handleGrossingChange}
              />
            )}
          </Modal.Body>

          {correctionScreen !== "preview" && (
            <Box
              px="md"
              py="sm"
              style={{
                borderTop: "1px solid var(--mantine-color-default-border)",
                flexShrink: 0,
              }}
            >
              <Group
                justify={
                  correctionScreen === "grossing" ? "flex-end" : "space-between"
                }
              >
                {correctionScreen !== "grossing" && (
                  <Group gap="sm">
                    <Button
                      variant="default"
                      onClick={onClose}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    {isCorrectionUX && onDiscard && (
                      <Button
                        color="red"
                        variant="light"
                        onClick={onDiscard}
                        disabled={isLoading}
                      >
                        Discard
                      </Button>
                    )}
                  </Group>
                )}
                {correctionScreen === "grossing" ? (
                  <Button onClick={handleReturnToCorrection}>
                    Done Editing Grossing
                  </Button>
                ) : (
                  <Group gap="sm">
                    {isCorrectionAmendmentMode && (
                      <Button
                        variant="light"
                        onClick={handleOpenGrossing}
                        disabled={isLoading}
                      >
                        Edit Grossing Details
                        {specimenChanges.length > 0
                          ? ` (${specimenChanges.length})`
                          : ""}
                      </Button>
                    )}
                    <Button
                      type="submit"
                      form="update-patient-info-form"
                      loading={isLoading}
                    >
                      {isCorrectionUX
                        ? specimenChanges.length > 0 ||
                          form.values.reportTimezoneCorrection
                          ? "Review Corrected Report"
                          : "Apply Correction"
                        : isEditMode
                        ? "Update Correction Request"
                        : isSignedOff
                        ? "Submit Correction Request"
                        : "Save Changes"}
                    </Button>
                  </Group>
                )}
              </Group>
            </Box>
          )}
        </Modal.Content>
      </Modal.Root>

      {correctionScreen === "preview" && correctionPreview && (
        <ReportPreviewModal
          key={correctionPreview.review.contentVersion.fingerprint}
          opened={opened}
          onClose={handleReturnFromPreview}
          onBack={handleReturnFromPreview}
          caseId={caseId}
          title="Review Corrected Report"
          previewDataOverride={correctedPreviewData}
          documentViewDataOverride={correctionPreview.documentViewData}
          forceTemplateId={correctionPreview.templateId ?? undefined}
          reviewRequired
          allowSignOutWhenFinalized
          showPrintAction={false}
          signOutLabel="Confirm Correction"
          signOutPending={directMutation.isPending}
          onAcknowledgeReview={handleAcknowledgeCorrectionPreview}
          onReviewContentChanged={handleCorrectionPreviewContentChanged}
          onSignOut={handleApplyPreviewedCorrection}
          blockOnUnavailableOrganMaps={false}
        />
      )}
    </>
  );
}
